/**
 * Simulations-Panel — zeigt die Twin-Reise (Blueprint „Der Twin trägt den Akt").
 *
 * Liest aus der Single Source (GET /api/cards/:kind/:id/simulate): (a) die MAPPE
 * des Twins (Artefakte/„Pässe"), (b) die RÄUME mit needs→produces + ehrlichem
 * Status (🟢/🔵/🔴/⚪) + Hängepunkt + LEGENDE.
 *
 * Live-Tracking: das Panel re-liest /simulate synchron mit dem Seiten-Poll
 * (refreshKey = WerkstattPage reloadKey; pollt während die Karte transient ist),
 * statt einer eigenen Schleife. So laufen Tabs UND Panel aus EINER Poll-Quelle.
 *
 * „Simulation neu starten":
 *  - Während die Pipeline LÄUFT (state ocr_pending/ocr_running/validated): Button
 *    deaktiviert, zeigt „● läuft…" + aktuellen Schritt + Laufzeit (kein verwirrendes
 *    „nochmal neu starten").
 *  - Im Ruhezustand: Dev/Demo-Karte → RESET (mit Bestätigung, danach trackt der
 *    Seiten-Poll den Fortschritt live). Echtkarte → seiteneffektfreier Re-Read.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { isDevCard } from "../../lib/devCards";
import { StatusLegend } from "../SettingsTestOcr";

type Sim = Awaited<ReturnType<typeof api.cards.simulate>>;

// states in denen die Maschine arbeitet (auto-Fortschritt) → Button „läuft", Poll aktiv
const MACHINE_WORKING = ["ocr_pending", "ocr_running", "validated"];

// Konsistente Status-Sprache (gleiche Glyphen/Farben wie StatusLegend + Tabs + Schritte).
// „active" wird gespalten: läuft (Maschine arbeitet) vs. wartet (manuelles Gate).
function gateGlyph(status: string, running: boolean): { glyph: string; color: string; label: string } {
  switch (status) {
    case "done": return { glyph: "✓", color: "#065f46", label: "fertig" };
    case "error": return { glyph: "✗", color: "#991b1b", label: "Fehler — hängt hier" };
    case "not-reached": return { glyph: "○", color: "#9ca3af", label: "noch nicht dran" };
    case "active": return running
      ? { glyph: "▶", color: "#1d4ed8", label: "läuft gerade" }
      : { glyph: "✋", color: "#b45309", label: "wartet auf dich" };
    default: return { glyph: "○", color: "#9ca3af", label: status };
  }
}

function fmtElapsed(s: number): string {
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function SimulationPanel({
  cardId, kind, cardState, refreshKey, onReset,
}: {
  cardId: string;
  kind: "timesheet" | "expense";
  cardState?: string;
  refreshKey?: number;
  onReset?: () => void;
}) {
  const [sim, setSim] = useState<Sim | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isDev = isDevCard(cardId);
  const running = !!cardState && MACHINE_WORKING.includes(cardState);

  // Laufzeit-Ticker: zählt sichtbar hoch solange die Pipeline läuft → klares
  // „es tut sich was"-Signal (Antwort auf „nur blau – tut sich was?").
  const runStart = useRef<number | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) { runStart.current = null; return; }
    if (runStart.current == null) runStart.current = Date.now();
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [running, cardState]);
  const elapsedS = runStart.current ? Math.floor((Date.now() - runStart.current) / 1000) : 0;

  // Re-Read synchron mit dem Seiten-Poll (refreshKey ändert sich bei jedem
  // WerkstattPage-Reload → während die Karte transient ist alle paar Sekunden).
  useEffect(() => {
    let alive = true;
    api.cards.simulate(kind, cardId)
      .then((r) => { if (alive) setSim(r); })
      .catch((e) => { if (alive) setErr(e?.message ?? String(e)); });
    return () => { alive = false; };
  }, [cardId, kind, refreshKey]);

  const doReset = useCallback(async () => {
    if (!window.confirm(
      "Dev-Karte zurücksetzen?\n\nAlle extrahierten Daten werden GELÖSCHT und die OCR startet "
      + "neu (wie ein frischer Upload). Die Phasen oben springen zurück und der Fortschritt wird "
      + "live mitverfolgt.\n\nNur für Test-Karten.",
    )) return;
    setBusy(true); setErr(null);
    try { await api.cards.reset(kind, cardId); onReset?.(); }
    catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setBusy(false); }
  }, [cardId, kind, onReset]);

  const doReRead = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setSim(await api.cards.simulate(kind, cardId)); }
    catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setBusy(false); }
  }, [cardId, kind]);

  const activeGate = sim?.gates.find((g) => g.status === "active");
  const currentStep = activeGate?.label ?? (sim?.hangs_at ? sim.gates.find((g) => g.gate === sim.hangs_at)?.label : null);

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 12, marginTop: 12, background: "#fafafa" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>🧪 Twin-Simulation</strong>
        {sim && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            Twin-Version {sim.twin_version}
          </span>
        )}
        <button type="button" onClick={isDev ? doReset : doReRead} disabled={busy || running}
          style={{ marginLeft: "auto", padding: "4px 12px", fontSize: 12, fontWeight: 600,
            background: (busy || running) ? "#9ca3af" : isDev ? "#b45309" : "#1d4ed8",
            color: "#fff", border: 0, borderRadius: 4, cursor: (busy || running) ? "default" : "pointer" }}
          title={running ? "Pipeline läuft — warte bis sie fertig ist"
            : isDev ? "Dev-Karte zurücksetzen (Daten löschen, OCR neu) und Fortschritt live verfolgen"
            : "Simulation seiteneffektfrei neu auswerten"}>
          {busy ? "…" : running ? "● läuft…" : "↻ Simulation neu starten"}
        </button>
      </div>

      {/* Klares „abgeschlossen"-Signal für den GANZEN Prozess (Antwort auf
          „ich brauche einen status wo ich weiß dass definitiv abgeschlossen ist"). */}
      {sim?.flows_through && !running && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700,
          color: "#065f46", background: "#ecfdf5", border: "1px solid #10b981",
          borderRadius: 4, padding: "6px 10px", marginBottom: 8 }}>
          🏁 Prozess vollständig abgeschlossen — alle Räume ✓, Twin durchgelaufen
        </div>
      )}

      {/* Live-Fortschrittsbanner: aktueller Schritt + Laufzeit (sichtbarer Tick). */}
      {running && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600,
          color: "#1e40af", background: "#eff6ff", border: "1px solid #bfdbfe",
          borderRadius: 4, padding: "6px 10px", marginBottom: 8 }}>
          <span style={{ color: "#16a34a" }}>●</span>
          <span>Pipeline läuft</span>
          {currentStep && <span>· aktueller Schritt: <strong>{currentStep}</strong></span>}
          <span style={{ color: "#6b7280", marginLeft: "auto" }}>
            state=<code>{cardState}</code> · läuft seit {fmtElapsed(elapsedS)}
          </span>
        </div>
      )}
      {isDev && !running && (
        <div style={{ fontSize: 11, color: "#b45309", marginBottom: 6 }}>
          ⚠ Dev-Karte: „Simulation neu starten" <strong>setzt die Karte zurück</strong> (Daten gelöscht, OCR neu) — die Phasen oben springen zurück und der Fortschritt wird live verfolgt.
        </div>
      )}

      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>Fehler: {err}</div>}
      {sim && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {/* Mappe des Twins */}
          <div style={{ flex: "1 1 240px", minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Mappe des Twins (Artefakte)</div>
            {sim.dossier.map((a) => (
              <div key={a.id} style={{ fontSize: 12, padding: "2px 0", color: a.present ? "#065f46" : "#9ca3af" }}>
                {a.present ? "✓" : "·"} <code>{a.id}</code>
                {a.present && a.stale && <span style={{ color: "#b45309" }}> (v{a.version} ⚠ stamp)</span>}
                <span style={{ color: "#9ca3af" }}> — {a.label}</span>
              </div>
            ))}
          </div>
          {/* Räume / emergenter Pfad */}
          <div style={{ flex: "2 1 360px", minWidth: 300 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Räume (Eingang → Ausgang)</div>
            {sim.gates.map((g) => {
              const gg = gateGlyph(g.status, running);
              const isActiveRunning = g.status === "active" && running;
              return (
                <div key={g.gate} style={{ fontSize: 12, padding: "3px 0", color: "#111827" }}>
                  <span style={{ color: gg.color, fontWeight: 700 }}>{gg.glyph}</span> <strong>{g.label}</strong>
                  {isActiveRunning && <span style={{ color: "#1e40af" }}> — läuft… ({fmtElapsed(elapsedS)})</span>}
                  {g.status === "active" && !running && <span style={{ color: "#b45309" }}> — wartet auf dich</span>}
                  <span style={{ color: "#9ca3af" }}>
                    {" "}[braucht: {g.needs.length ? g.needs.join(", ") : "—"} → stellt aus: {g.produces}]
                  </span>
                  <div style={{ marginLeft: 18, color: "#6b7280" }}>{g.reason}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legende — Single Source (gleiche Sprache wie Tabs + Schritte) */}
      <StatusLegend />
    </div>
  );
}
